import constants from 'app/constants'
import { LOCAL_STORAGE } from 'app/localStorageManager'
import { getBlockchainErrorStatus } from 'app/util/errorCodes'
import fetchJSONData from 'app/util/fetchJSONData'
import useLogger from 'lib/util/useLogger'
import { API_RESPONSE } from './constants'
import getSignature from './getSignature'

const debug = useLogger('Auth', 'violet')

/**
 * Attempt to login a user with a JWT token
 *
 * @param {string} address - the users wallet address
 * @returns {object?} the result or null if fail
 */
const loginWithJWT = async address => {
  // Check for presence of token in local storage
  const token = localStorage.getItem(LOCAL_STORAGE.userTokenCache)

  if (!token) {
    // No token
    return null
  }

  const { data } = await fetchJSONData(
    `${constants.API_URL}/wl/signedup?wallet=${address}&network=${constants.NETWORK}`,
    {
      headers: {
        'company-id': constants.API_ID,
        'Content-Type': 'application/json',
        'x-access-token': token
      }
    },
    false
  )

  if (
    data?.message === API_RESPONSE.JWT_EXPIRED ||
    data?.message === API_RESPONSE.JWT_INVALID
  ) {
    // Token was expired or invalid, remove it from local storage
    localStorage.removeItem(LOCAL_STORAGE.userTokenCache)
    debug.log('JWT expired')

    return null
  }

  if (data?.token) {
    // Has token, add it to local storage
    localStorage.setItem(LOCAL_STORAGE.userTokenCache, data.token)
  }

  if (
    data?.message === API_RESPONSE.NOT_SIGNED_UP ||
    data?.message === API_RESPONSE.LOGGED_IN
  ) {
    // When match either of these messages, consider it
    // a successful API call. If not, chance of a bad/unknown
    // response, so assume the JWT failed
    return data
  }

  return null
}

/**
 * Attempt to login a user with a signature (metamask)
 *
 * @param {string} address - the user address
 * @param {object} signer - the signer info
 * @param {Function} setAppActivityLabel - sets the activity label
 * @returns {object?} the result or null if fail
 */
const loginWithSignature = async (address, signer, setAppActivityLabel) => {
  setAppActivityLabel('Requesting signature')
  debug.log(signer)

  const signature = await getSignature(address, signer)

  setAppActivityLabel('Signature received')

  if (!signature) {
    // Fail
    return null
  }

  const { data } = await fetchJSONData(
    `${constants.API_URL}/wl/signedup?wallet=${address}&network=${constants.NETWORK}`,
    {
      headers: {
        'company-id': constants.API_ID,
        'Content-Type': 'application/json',
        'X-Signature': signature
      }
    },
    false
  )

  if (data?.token) {
    // Has token, add it to local storage
    // NOTE: token is only provided if the user has logged in
    localStorage.setItem(LOCAL_STORAGE.userTokenCache, data.token)
  }

  return data
}

/**
 * Get the user account
 *
 * @param {string} address - the address
 * @param {Function} getSigner - the signer function
 * @param {Function} setAppActivityLabel - activity label
 * @returns {object} the result
 */
const getAccount = async (address, getSigner, setAppActivityLabel) => {
  if (!address) {
    // No user info from wallet
    return {}
  }

  let res = null
  let methodUsedLog = 'JWT'
  let errorStatus = {
    message: 'An unknown error occurred'
  }

  try {
    // Attempt load with JWT first
    res = await loginWithJWT(address)

    if (!res) {
      methodUsedLog = 'signature'

      debug.log('Awaiting signer')

      const signer = await getSigner()

      debug.log('Got a signer', signer)
      // Failed with JWT, attempt load with a signature
      res = await loginWithSignature(address, signer, setAppActivityLabel)
    }
  } catch (e) {
    // Fail
    errorStatus = getBlockchainErrorStatus(e)
  }

  if (res?.message === API_RESPONSE.NOT_SIGNED_UP) {
    // User isn't signed up
    debug.log(`Not signed up (via ${methodUsedLog})`, res)

    return {
      isSignedIn: false,
      isPassportHolder: !!res.passport_holder,
      user: res.user
    }
  }

  if (res?.message === API_RESPONSE.LOGGED_IN) {
    debug.log(`Signed in (via ${methodUsedLog})`)

    // User was signed in
    return {
      isSignedIn: true,
      isPassportHolder: !!res.user?.passport_holder,
      user: res.user
    }
  }

  // Handle all other errors
  return {
    hasAccountLoginError: true,
    accountLoginErrorMessage: errorStatus?.message
  }
}

export default getAccount
